import { ButtonGroup, Card, Col, Container, Dropdown, DropdownButton, Row } from "react-bootstrap";
import { useInvoiceQuery, useQuickbooksQuery } from "../../../../queries";
import { DashboardMetric, DistributionLinks, InvoiceDashboardViews, InvoiceLinks, InvoiceReportSlugs, InvoicingDashboardInvoices, InvoicingDashboardTissues } from "../..";
import { useStoredState } from "../../../../hooks";
import { AutoCol, fas, Icon, LoadingSpinner } from "../../../../component-library";
import { useNavigate } from "react-router-dom";
import { KeyboardEvent } from "react";
import React from "react";

export function InvoicingDashboard() {
    const navigate = useNavigate();
    const { syncUnpaidInvoicesQuery } = useQuickbooksQuery({ queryTypes: ["sync"] });
    const {
        dashboardQuery: { data, isFetching },
    } = useInvoiceQuery({ queryType: "dashboard" });
    const [view, setView] = useStoredState<InvoiceDashboardViews>(
        "invoice-dashboard-view",
        InvoiceDashboardViews.TissuesReady
    );

    const handleSearchKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            navigate("/invoices");
        }
    };

    const isLoading = isFetching || syncUnpaidInvoicesQuery.isFetching;

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <Container>
            <h1>Invoicing Dashboard</h1>
            <Row className="gx-3 mb-3 d-flex" >
                <Col className="mb-2 d-flex" >
                    <Card className="bg-primary text-white flex-fill">
                        <Card.Header>Tissues</Card.Header>
                        <Card.Body className="bg-canvas">
                            <Row>
                                <Col>
                                    <DashboardMetric
                                        title="Ready to Invoice"
                                        value={data?.tissuesReadyForInvoice?.length ?? "-"}
                                        onClick={() => setView(InvoiceDashboardViews.TissuesReady)}
                                        isActive={view === InvoiceDashboardViews.TissuesReady}
                                    />
                                </Col>
                                <Col>
                                    <DashboardMetric
                                        title="Not Ready to Invoice"
                                        value={data?.tissuesNotReadyForInvoice?.length ?? "-"}
                                        onClick={() => setView(InvoiceDashboardViews.TissuesNotReady)}
                                        isActive={view === InvoiceDashboardViews.TissuesNotReady}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col className="mb-2 d-flex">
                    <Card className="bg-primary text-white flex-fill">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Invoices</span>
                                <Icon
                                    icon={fas.faSearch}
                                    tabIndex={0}
                                    onClick={() => navigate("/invoices")}
                                    inverse
                                    className="clickable"
                                    title="Search All Invoices"
                                    aria-label="Search All Invoices"
                                    onKeyDown={handleSearchKeyDown}
                                />
                            </div>
                        </Card.Header>
                        <Card.Body className="bg-canvas">
                            <Row>
                                <Col>
                                    <DashboardMetric
                                        title="Ready for QuickBooks"
                                        value={data?.invoicesReadyForQbo?.length ?? "-"}
                                        onClick={() => setView(InvoiceDashboardViews.InvoicesReady)}
                                        isActive={view === InvoiceDashboardViews.InvoicesReady}
                                    />
                                </Col>
                                <Col>
                                    <DashboardMetric
                                        title="Flagged"
                                        value={data?.flaggedInvoices?.length ?? "-"}
                                        onClick={() => setView(InvoiceDashboardViews.InvoicesFlagged)}
                                        isActive={view === InvoiceDashboardViews.InvoicesFlagged}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <AutoCol className="mb-2 d-flex">
                    <InvoiceLinks />
                </AutoCol>
            </Row>
            {view === InvoiceDashboardViews.TissuesReady && (
                <InvoicingDashboardTissues tissues={data?.tissuesReadyForInvoice ?? []} view={view} />
            )}
            {view === InvoiceDashboardViews.TissuesNotReady && (
                <InvoicingDashboardTissues tissues={data?.tissuesNotReadyForInvoice ?? []} view={view} />
            )}
            {view === InvoiceDashboardViews.InvoicesReady && (
                <InvoicingDashboardInvoices invoices={data?.invoicesReadyForQbo ?? []} view={view} />
            )}
            {view === InvoiceDashboardViews.InvoicesFlagged && (
                <InvoicingDashboardInvoices invoices={data?.flaggedInvoices ?? []} view={view} />
            )}
        </Container>
    );
}
